import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { FiMenu, FiMoon, FiSun, FiX } from 'react-icons/fi';
import ViewsCounter from './ViewsCounter';
// import AdSlotPlaceholder from './AdSlotPlaceholder';
import { getCachedGlobalViewCount, getGlobalViewCount, incrementGlobalView } from '../services/metrics';
import { getTheme, toggleTheme } from '../services/theme';

const primaryLinks = [
  { to: '/gpa-calculator', label: 'GPA' },
  { to: '/cgpa-calculator', label: 'CGPA' },
  { to: '/grade-converter', label: 'Converter' },
  { to: '/faq', label: 'FAQ' }
];

const legalLinks = [
  { to: '/about', label: 'About' },
  { to: '/privacy-policy', label: 'Privacy Policy' },
  { to: '/terms-and-conditions', label: 'Terms & Conditions' },
  { to: '/contact', label: 'Contact' },
  { to: '/disclaimer', label: 'Disclaimer' }
];

export default function Layout({ children }) {
  const location = useLocation();
  const [viewCount, setViewCount] = useState(getCachedGlobalViewCount);
  const [theme, setTheme] = useState(getTheme());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const viewTrackingRequestRef = useRef(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    if (!viewTrackingRequestRef.current) {
      viewTrackingRequestRef.current = incrementGlobalView()
        .catch(() => null)
        .then(getGlobalViewCount);
    }

    viewTrackingRequestRef.current.then((latest) => {
      if (isMounted && typeof latest === 'number') setViewCount(latest);
    });

    return () => { isMounted = false; };
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="relative border-b border-brand-border bg-brand-surface/90 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <Link to="/" className="text-xl font-bold tracking-tight text-brand-text">GradeVIT</Link>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setTheme(toggleTheme())}
                className="rounded-xl border border-brand-border bg-brand-surface p-2 text-brand-muted hover:text-brand-text"
                aria-label="Toggle theme"
                title="Toggle theme"
              >
                {theme === 'dark' ? <FiSun size={16} /> : <FiMoon size={16} />}
              </button>

              <nav className="hidden items-center gap-1 rounded-xl border border-brand-border bg-brand-accentSoft p-1 md:flex">
                {primaryLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium ${isActive ? 'bg-brand-accent text-white' : 'text-brand-text hover:bg-brand-surface'}`}
                  >
                    {link.label}
                  </NavLink>
                ))}
              </nav>

              <button
                type="button"
                className="rounded-xl border border-brand-border bg-brand-surface p-2 text-brand-muted md:hidden"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label="Toggle navigation menu"
                title="Toggle navigation menu"
              >
                {mobileMenuOpen ? <FiX size={18} /> : <FiMenu size={18} />}
              </button>
            </div>
          </div>
        </div>

        <nav
          className={`absolute left-0 top-full z-50 w-full origin-top border-b border-brand-border bg-brand-surface px-4 py-3 transition-all duration-300 ease-in-out md:hidden ${mobileMenuOpen ? 'opacity-100 scale-y-100 pointer-events-auto' : 'opacity-0 scale-y-0 pointer-events-none'}`}
        >
          <div className="mx-auto grid max-w-6xl gap-2">
            {primaryLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `rounded-xl border border-brand-border px-3 py-2 text-center text-sm font-medium ${isActive ? 'bg-brand-accent text-white' : 'bg-brand-surface text-brand-text'}`}
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-4 py-8 pb-40">
          <div className="flex items-start gap-6">
            <div className="min-w-0 flex-1">{children}</div>
            {/* Ads Placeholder */}

            {/* <aside className="sticky top-24 hidden w-72 shrink-0 xl:block">
              <AdSlotPlaceholder label="Advertisement" className="min-h-40 max-h-40 rounded-2xl" />
            </aside> */}

          </div>
        </div>
      </main>

      <footer className="border-t border-brand-border bg-brand-surface/90">
        <div className="mx-auto max-w-6xl space-y-4 px-4 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-3 text-sm text-brand-muted">
              {legalLinks.map((link) => (<Link key={link.to} to={link.to} className="underline-offset-2 hover:underline">{link.label}</Link>))}
            </div>
            <ViewsCounter count={viewCount} />
          </div>
          <p className="text-center text-xs text-brand-muted">GradeVIT is not an official VIT website.</p>
        </div>
      </footer>
    </div>
  );
}
